import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "templateList", "formArea", "formTitle",
    "unsavedOverlay",
    "saveThumb", "saveThumbTitle", "saveThumbSub", "saveThumbBadge",
    "libraryView", "libraryGrid", "libraryPills",
    "sidebarTooltip", "mobileBar",
    "hintColour", "hintName", "hintLogo", "hintSignup",
    "stepName", "stepLogo"
  ]

  static values = {
    templates: { type: Array, default: [] }
  }

  connect() {
    this.templates = this.templatesValue
    this.categories = this.buildCategories()
    this.activeCategory = 'All'
    this.currentTemplateId = this.templates.length > 0 ? this.templates[0].id : null
    this.pendingTemplateId = null
    this.brandColor = '#1a6b4a'
    this.editedFields = new Set()
    this.fieldValues = {}
    this.activeLibraryCategory = 'All'
    this.onboardingStep = 0
    this.companyName = 'Henley Marquee Hire'
    this.useTextLogo = false

    this.renderTemplateList()
    this.renderForm()
    this.applyBrandColor()
    this.showSidebarTooltip()
    this.showLibrary()
  }

  // -- Categories -----------------------------------------------------------

  buildCategories() {
    const cats = new Set()
    this.templates.forEach(t => cats.add(t.category || 'General'))
    return ['All', ...Array.from(cats).sort()]
  }

  // -- Template list --------------------------------------------------------

  renderTemplateList() {
    let filtered = this.templates

    if (this.activeCategory !== 'All') {
      filtered = filtered.filter(t => (t.category || 'General') === this.activeCategory)
    }

    // Category dropdown
    const options = this.categories.map(cat =>
      `<option value="${this.esc(cat)}" ${cat === this.activeCategory ? 'selected' : ''}>${this.esc(cat === 'All' ? 'All business types' : cat)}</option>`
    ).join('')

    // Template items
    const items = filtered.map(t => {
      const fieldsText = t.fields_count ? `${t.fields_count} fields` : ''
      const usesText = t.uses ? `${t.uses} uses` : ''
      const metaParts = [fieldsText, usesText].filter(Boolean).join(' · ')

      return `
        <button class="db-tpl-item ${t.id === this.currentTemplateId ? 'db-tpl-item--active' : ''}"
                data-action="click->docs-builder#selectTemplate"
                data-docs-builder-template-id-param="${t.id}">
          <span class="db-tpl-item__info">
            <span class="db-tpl-item__name">${this.esc(t.name)}</span>
            <span class="db-tpl-item__meta">
              <span class="db-tpl-item__category">${this.esc(t.category || 'General')}</span>
              ${metaParts ? `<span class="db-tpl-item__fields">${metaParts}</span>` : ''}
            </span>
          </span>
        </button>
      `
    }).join('')

    const countText = `${filtered.length} template${filtered.length !== 1 ? 's' : ''}`
    this.templateListTarget.innerHTML = `
      <div class="db-category-select-wrap">
        <label class="db-category-select__label">Select your business type</label>
        <select class="db-category-select"
                data-action="change->docs-builder#selectCategory">
          ${options}
        </select>
      </div>
      <div class="db-tpl-count">${countText}</div>
      <div class="db-tpl-items">${items}</div>
    `
  }

  selectCategory(event) {
    this.activeCategory = event.target.value
    this.renderTemplateList()
  }

  selectTemplate(event) {
    const id = event.params.templateId
    if (id === this.currentTemplateId) return

    if (this.editedFields.size > 0) {
      this.pendingTemplateId = id
      this.openUnsavedModal()
    } else {
      this.switchTemplate(id)
    }
  }

  switchTemplate(id) {
    this.currentTemplateId = id
    this.editedFields.clear()
    this.fieldValues = {}
    const tpl = this.getCurrentTemplate()
    if (tpl) this.formTitleTarget.value = tpl.name
    this.renderTemplateList()
    this.renderForm()
  }

  // -- Form rendering -------------------------------------------------------

  renderForm() {
    const tpl = this.getCurrentTemplate()
    if (!tpl) {
      this.formAreaTarget.innerHTML = '<div class="db-empty">No template selected</div>'
      return
    }

    const sections = tpl.sections || []
    const logoHtml = this.useTextLogo
      ? `<div class="db-form-header__text-logo">${this.esc(this.companyName)}</div>`
      : `<div class="db-form-header__logo">${this.esc(this.companyName)}</div>`

    this.formAreaTarget.innerHTML = `
      <div class="db-form-header" style="background:${this.brandColor}">
        ${logoHtml}
        <h2 class="db-form-header__title">${this.esc(tpl.name)}</h2>
        <p class="db-form-header__sub">Please complete all sections below</p>
      </div>
      ${sections.map((sec, si) => this.renderSection(sec, si)).join('')}
    `

    this.renderPdfViewers()
  }

  renderSection(section, sectionIndex) {
    const cols = section.columns || 1
    const gridClass = cols === 2 ? 'db-fields--grid' : ''
    const fields = section.fields || []

    return `
      <div class="db-section" data-section-index="${sectionIndex}">
        <div class="db-section__header">
          <h3 class="db-section__title" style="color:${this.brandColor}">${this.esc(section.title)}</h3>
          <button class="db-section__col-toggle"
                  data-action="click->docs-builder#toggleColumns"
                  data-docs-builder-section-index-param="${sectionIndex}"
                  title="Toggle columns">
            ${cols === 2 ? '1 col' : '2 col'}
          </button>
        </div>
        <div class="db-fields ${gridClass}">
          ${fields.map((f, fi) => this.renderField(f, sectionIndex, fi)).join('')}
        </div>
      </div>
    `
  }

  renderField(field, si, fi) {
    const key = `${si}-${fi}`
    const val = this.fieldValues[key] || ''
    const ph = field.placeholder || ''
    const label = field.label || ''

    let input = ''
    switch (field.type) {
      case 'text': case 'email': case 'phone':
        input = `<input type="${field.type === 'phone' ? 'tel' : field.type}" class="db-field__input" placeholder="${this.esc(ph)}" value="${this.esc(val)}" data-action="input->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">`
        break
      case 'number':
        input = `<input type="number" class="db-field__input" placeholder="${this.esc(ph)}" value="${this.esc(val)}" data-action="input->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">`
        break
      case 'date': case 'time':
        input = `<input type="${field.type}" class="db-field__input" value="${this.esc(val)}" data-action="input->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">`
        break
      case 'textarea':
        input = `<textarea class="db-field__input db-field__input--textarea" placeholder="${this.esc(ph)}" data-action="input->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">${this.esc(val)}</textarea>`
        break
      case 'select':
        input = `<select class="db-field__input db-field__input--select" data-action="change->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">
          ${(field.options || []).map(o => `<option ${val === o ? 'selected' : ''}>${this.esc(o)}</option>`).join('')}
        </select>`
        break
      case 'radio':
        input = `<div class="db-field__radio-group">
          ${(field.options || []).filter(o => o !== '\u2014').map(o => `
            <label class="db-field__radio">
              <input type="radio" name="field-${key}" value="${this.esc(o)}" ${val === o ? 'checked' : ''} data-action="change->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">
              <span>${this.esc(o)}</span>
            </label>
          `).join('')}
        </div>`
        break
      case 'checkbox':
        const chk = val === 'true' ? 'checked' : ''
        input = `<label class="db-field__check">
          <input type="checkbox" ${chk} data-action="change->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">
          <span>${this.esc(label)}</span>
        </label>`
        return `<div class="db-field db-field--checkbox">${input}</div>`
      case 'rating':
        input = `<div class="db-field__rating">
          ${[1,2,3,4,5].map(n => `<span class="db-field__star">${n <= (parseInt(val) || 0) ? '★' : '☆'}</span>`).join('')}
        </div>`
        break
      case 'signature':
        input = `<div class="db-field__file-zone">Signature area</div>`
        break
      case 'rich_textarea':
        if (field.content) {
          input = `<div class="db-field__rich-content">${field.content}</div>`
        } else {
          input = `<div class="db-field__rich-content db-field__rich-content--empty"><em>Rich text content will be displayed here...</em></div>`
        }
        break
      case 'pdf':
        if (field.pdf_url) {
          input = `<div class="db-field__pdf-viewer" data-pdf-url="${field.pdf_url}">
            <div class="db-field__pdf-toolbar">
              <span class="db-field__pdf-icon"><i class="bi bi-file-earmark-pdf"></i></span>
              <span class="db-field__pdf-filename">${this.esc(field.pdf_filename || 'Document.pdf')}</span>
              <span class="db-field__pdf-pages"></span>
            </div>
            <div class="db-field__pdf-canvas-container">
              <div class="db-field__pdf-loading">Loading PDF...</div>
            </div>
          </div>`
        } else {
          input = `<div class="db-field__file-zone">PDF document will be displayed here...</div>`
        }
        break
      case 'file':
        input = `<div class="db-field__file-zone">Click or drag to upload</div>`
        break
      default:
        input = `<input type="text" class="db-field__input" placeholder="${this.esc(ph)}" value="${this.esc(val)}" data-action="input->docs-builder#trackEdit" data-docs-builder-field-key-param="${key}">`
    }

    return `
      <div class="db-field">
        <label class="db-field__label">${this.esc(label)}</label>
        ${input}
      </div>
    `
  }

  // -- Brand color ----------------------------------------------------------

  selectColor(event) {
    this.brandColor = event.params.color
    this.applyBrandColor()

    this.element.querySelectorAll('.docs-builder__swatch').forEach(s => s.classList.remove('docs-builder__swatch--active'))
    event.currentTarget.classList.add('docs-builder__swatch--active')

    this.renderForm()
    this.advanceOnboarding(2)
  }

  customColor() {
    const color = prompt('Enter a hex colour (e.g. #ff6600):')
    if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
      this.brandColor = color
      this.element.querySelectorAll('.docs-builder__swatch').forEach(s => s.classList.remove('docs-builder__swatch--active'))
      this.applyBrandColor()
      this.renderForm()
      this.advanceOnboarding(2)
    }
  }

  applyBrandColor() {
    this.element.style.setProperty('--db-brand', this.brandColor)
  }

  // -- Edit tracking --------------------------------------------------------

  trackEdit(event) {
    const key = event.params.fieldKey
    if (!key) return

    const el = event.target
    const val = el.type === 'checkbox' ? String(el.checked) : el.value

    if (val && val !== '' && val !== 'false' && val !== '\u2014') {
      this.editedFields.add(key)
      this.fieldValues[key] = val
    } else {
      this.editedFields.delete(key)
      delete this.fieldValues[key]
    }

    this.updateSessionBar()
  }

  countEditedFields() {
    return this.editedFields.size
  }

  updateSessionBar() {
    const count = this.countEditedFields()
    const statusEl = this.element.querySelector('.docs-builder__session-status')
    if (statusEl) {
      statusEl.textContent = count > 0 ? `${count} field${count !== 1 ? 's' : ''} edited` : 'Draft preserved'
    }
  }

  // -- Column toggle --------------------------------------------------------

  toggleColumns(event) {
    const si = parseInt(event.params.sectionIndex)
    const tpl = this.getCurrentTemplate()
    if (!tpl) return
    const sec = tpl.sections[si]
    if (!sec) return

    sec.columns = sec.columns === 2 ? 1 : 2
    this.renderForm()
  }

  addSection() {
    this.openSaveModal()
  }

  // -- Unsaved changes modal ------------------------------------------------

  openUnsavedModal() {
    this.unsavedOverlayTarget.style.display = 'flex'
  }

  closeUnsavedModal() {
    this.unsavedOverlayTarget.style.display = 'none'
    this.pendingTemplateId = null
  }

  discardAndSwitch() {
    this.unsavedOverlayTarget.style.display = 'none'
    if (this.pendingTemplateId) {
      this.switchTemplate(this.pendingTemplateId)
      this.pendingTemplateId = null
    }
  }

  // -- Save / Send modal (delegates to shared signup-modal) -----------------

  openSaveModal() {
    if (this.hasHintSignupTarget) this.hintSignupTarget.classList.add('d-none')

    const tpl = this.getCurrentTemplate()
    const count = this.countEditedFields()

    if (this.hasSaveThumbTitleTarget && tpl) this.saveThumbTitleTarget.textContent = tpl.name
    if (this.hasSaveThumbSubTarget) this.saveThumbSubTarget.textContent = `Your edits - ${count} field${count !== 1 ? 's' : ''} filled`
    if (this.hasSaveThumbBadgeTarget) {
      this.saveThumbBadgeTarget.textContent = 'Saving your version'
      this.saveThumbBadgeTarget.className = 'signup-modal__thumb-badge'
    }
    if (this.hasSaveThumbTarget) this.saveThumbTarget.className = 'signup-modal__thumb'

    const signupModal = this.element.querySelector('[data-controller="signup-modal"]')
    if (signupModal) {
      const controller = this.application.getControllerForElementAndIdentifier(signupModal, 'signup-modal')
      if (controller) {
        // Hand over the template they picked. SignupsController clones it into their library
        // on confirmation, so this is the difference between landing in Event Docs on the doc
        // they just built and landing on an empty account.
        controller.templateGuidValue = this.currentTemplateId || ''
        controller.open()
      }
    }
  }

  // -- Library view ---------------------------------------------------------

  showLibrary() {
    if (this.hasLibraryViewTarget) {
      this.element.querySelector('.docs-builder__layout').style.display = 'none'
      this.libraryViewTarget.style.display = 'flex'
      this.activeLibraryCategory = 'All'
      this.renderLibraryPills()
      this.renderLibraryGrid()
      this.toggleMobileBar(false)
    }
  }

  hideLibrary() {
    if (this.hasLibraryViewTarget) {
      this.libraryViewTarget.style.display = 'none'
      this.element.querySelector('.docs-builder__layout').style.display = 'flex'
      this.advanceOnboarding(1)
      this.toggleMobileBar(true)
    }
  }

  // The mobile bar belongs to the builder, not the library: its swatches colour a form that
  // is not open yet, and "save this form" reads as nonsense while the visitor is still
  // choosing one. Above 900px the stylesheet keeps it hidden either way.
  toggleMobileBar(visible) {
    if (!this.hasMobileBarTarget) return

    this.mobileBarTarget.classList.toggle('docs-builder__mobile-bar--hidden', !visible)
  }

  renderLibraryGrid() {
    if (!this.hasLibraryGridTarget) return

    let filtered = this.templates

    if (this.activeLibraryCategory !== 'All') {
      filtered = filtered.filter(t => (t.category || 'General') === this.activeLibraryCategory)
    }

    // Group by category (same as _event_docs_demo.html.haml)
    const grouped = {}
    filtered.forEach(t => {
      const cat = t.category || 'General'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(t)
    })

    const groups = Object.entries(grouped).map(([category, templates]) => {
      const count = templates.length
      const countLabel = `${count} template${count !== 1 ? 's' : ''}`

      const avatarColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444']

      const cards = templates.map(t => {
        const badge = t.verified && t.supplier_name
          ? `<div class="ge-docs-card__badge">${this.esc(t.supplier_name)}</div>`
          : ''

        const initials = (t.social_proof_initials || [])
        const avatars = initials.map((initial, i) =>
          `<span class="avatar avatar-${i + 1}">${this.esc(initial)}</span>`
        ).join('')

        const usesLabel = t.uses > 0 ? `<span class="submissions-count">${t.uses} use${t.uses !== 1 ? 's' : ''}</span>` : ''

        const socialProof = (avatars || usesLabel)
          ? `<div class="social-proof-footer"><div class="avatar-stack">${avatars}</div>${usesLabel}</div>`
          : ''

        return `
          <button class="ge-docs-card" data-action="click->docs-builder#selectLibraryTemplate" data-docs-builder-template-id-param="${t.id}">
            <div class="ge-docs-card__preview">
              <div class="ge-docs-card__doc">
                <div class="ge-docs-card__doc-line"></div>
                <div class="ge-docs-card__doc-line"></div>
                <div class="ge-docs-card__doc-line"></div>
                <div class="ge-docs-card__doc-line"></div>
                <div class="ge-docs-card__doc-line"></div>
              </div>
              ${badge}
            </div>
            <div class="ge-docs-card__body">
              <div class="ge-docs-card__title">${this.esc(t.name)}</div>
              <div class="ge-docs-card__meta">
                ${socialProof}
              </div>
            </div>
          </button>
        `
      }).join('')

      return `
        <div class="ge-docs-demo__group">
          <div class="ge-docs-demo__group-header">
            <h4 class="ge-docs-demo__group-title">${this.esc(category)}</h4>
            <span class="ge-docs-demo__group-count">${countLabel}</span>
          </div>
          <div class="ge-docs-demo__group-grid ge-docs-demo__group-grid--wrap">${cards}</div>
        </div>
      `
    }).join('')

    const totalCount = filtered.length
    const countText = `${totalCount} template${totalCount !== 1 ? 's' : ''} available`
    this.libraryGridTarget.innerHTML = `
      <div class="docs-builder__library-count">${countText}</div>
      <div class="ge-docs-demo__groups">${groups}</div>
    `
  }

  renderLibraryPills() {
    if (!this.hasLibraryPillsTarget) return

    const pills = this.categories.map(cat => {
      const active = cat === this.activeLibraryCategory ? 'ge-category-pill--active' : ''
      return `
        <button class="ge-category-pill ${active}"
                data-action="click->docs-builder#filterLibraryCategory"
                data-docs-builder-category-name-param="${this.esc(cat)}">
          ${this.esc(cat)}
        </button>
      `
    }).join('')

    this.libraryPillsTarget.innerHTML = `<div class="ge-category-pills">${pills}</div>`
  }

  filterLibraryCategory(event) {
    this.activeLibraryCategory = event.params.categoryName
    this.renderLibraryPills()
    this.renderLibraryGrid()
  }

  selectLibraryTemplate(event) {
    const id = event.params.templateId
    this.hideLibrary()
    this.switchTemplate(id)
  }

  // -- Sidebar tooltip ------------------------------------------------------

  showSidebarTooltip() {
    if (!this.hasSidebarTooltipTarget) return
    if (localStorage.getItem('db-tooltip-dismissed')) return

    this.sidebarTooltipTarget.style.display = 'block'
  }

  dismissTooltip() {
    if (!this.hasSidebarTooltipTarget) return

    localStorage.setItem('db-tooltip-dismissed', 'true')
    this.sidebarTooltipTarget.style.display = 'none'
  }

  // -- Onboarding hints -----------------------------------------------------

  editCompanyName(event) {
    this.companyName = event.target.value.trim() || 'Your Company'
    this.renderForm()
    this.advanceOnboarding(3)
  }

  useNameAsLogo() {
    this.useTextLogo = true
    this.renderForm()
    this.advanceOnboarding(4)
  }

  advanceOnboarding(step) {
    if (step <= this.onboardingStep) return
    this.onboardingStep = step

    // Hide all hints
    const hints = ['hintColour', 'hintName', 'hintLogo', 'hintSignup']
    hints.forEach(h => { if (this[`has${h.charAt(0).toUpperCase() + h.slice(1)}Target`]) this[`${h}Target`].classList.add('d-none') })

    // Step 1: Show colour hint (arrow pointing at swatches)
    if (step === 1 && this.hasHintColourTarget) {
      this.hintColourTarget.classList.remove('d-none')
    }

    // Step 2: Reveal company name section + hint
    if (step >= 2) {
      if (this.hasStepNameTarget) this.stepNameTarget.classList.remove('d-none')
      if (step === 2 && this.hasHintNameTarget) this.hintNameTarget.classList.remove('d-none')
    }

    // Step 3: Reveal logo section + hint
    if (step >= 3) {
      if (this.hasStepLogoTarget) this.stepLogoTarget.classList.remove('d-none')
      if (step === 3 && this.hasHintLogoTarget) this.hintLogoTarget.classList.remove('d-none')
    }

    // Step 4: Show signup CTA hint
    if (step === 4 && this.hasHintSignupTarget) {
      this.hintSignupTarget.classList.remove('d-none')
    }
  }

  // -- PDF.js canvas rendering ----------------------------------------------

  renderPdfViewers() {
    const viewers = this.formAreaTarget.querySelectorAll('.db-field__pdf-viewer[data-pdf-url]')
    if (viewers.length === 0) return

    this.loadPdfJs().then(() => {
      viewers.forEach(viewer => this.initPdfViewer(viewer))
    }).catch(() => {
      viewers.forEach(viewer => {
        const loading = viewer.querySelector('.db-field__pdf-loading')
        if (loading) loading.textContent = 'Unable to load PDF viewer'
      })
    })
  }

  loadPdfJs() {
    if (typeof pdfjsLib !== 'undefined') return Promise.resolve()

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      script.onload = () => {
        if (typeof pdfjsLib !== 'undefined') {
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
          resolve()
        } else {
          reject()
        }
      }
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  initPdfViewer(viewer) {
    const url = viewer.dataset.pdfUrl
    const container = viewer.querySelector('.db-field__pdf-canvas-container')
    const loading = viewer.querySelector('.db-field__pdf-loading')
    const pagesLabel = viewer.querySelector('.db-field__pdf-pages')

    pdfjsLib.getDocument({ url, withCredentials: true }).promise.then(pdf => {
      if (loading) loading.remove()
      if (pagesLabel) pagesLabel.textContent = `${pdf.numPages} page${pdf.numPages !== 1 ? 's' : ''}`

      for (let i = 1; i <= pdf.numPages; i++) {
        this.renderPdfPage(pdf, i, container)
      }
    }).catch(() => {
      if (loading) loading.textContent = 'Failed to load PDF'
    })
  }

  renderPdfPage(pdf, pageNum, container) {
    pdf.getPage(pageNum).then(page => {
      const containerWidth = container.clientWidth - 40
      const viewport = page.getViewport({ scale: 1.0 })
      const dpr = window.devicePixelRatio || 1
      const scale = (containerWidth / viewport.width) * dpr
      const scaledViewport = page.getViewport({ scale })

      const wrapper = document.createElement('div')
      wrapper.style.marginBottom = '20px'

      const canvas = document.createElement('canvas')
      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height
      canvas.style.width = (scaledViewport.width / dpr) + 'px'
      canvas.style.height = (scaledViewport.height / dpr) + 'px'
      canvas.style.display = 'block'
      canvas.style.margin = '0 auto'

      wrapper.appendChild(canvas)
      container.appendChild(wrapper)

      page.render({ canvasContext: canvas.getContext('2d'), viewport: scaledViewport })
    })
  }

  // -- Helpers --------------------------------------------------------------

  getCurrentTemplate() {
    return this.templates.find(t => t.id === this.currentTemplateId) || this.templates[0] || null
  }

  esc(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
